import { Fragment, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchForm, setForm as setGlobalForm } from '../store/form.slice'
import { resetSubmission, setPayload, verifySubmission } from '../store/submission.slice'
import { ConfirmResponseView } from './confirm-response.view'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { NavBack } from '../components/nav-back'
import { getDirtySubmissionForm, manageSet, setToCalendarDate } from '../utils/functions'
import { AnswerTypeSchema, QuestionSchema, SectionSchema } from '../utils/types/form'
import { NewSubmissionSchema, SubmissionSchema } from '../utils/types/submission'
import { TEXT_AREA_ROWS } from '../utils/constants'

export const SubmitFormView = () => {
  const [existingSubmission, setExistingSubmission] = useState<SubmissionSchema | null>(null)
  const [answer, setAnswer] = useState<AnswerTypeSchema>('')
  /* const [filesData, setFilesData] = useState<Record<string, any>[]>([]) */
  const [allQuestions, setAllQuestions] = useState<QuestionSchema[]>([])
  const allCheckboxes = useRef(new Map<string, Set<string>>())
  const [isCheckboxMapReady, setIsCheckboxMapReady] = useState(false)
  const [isLoadingForm, setIsLoadingForm] = useState(false)
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false)
  const [requestState, setRequestState] = useState({ error: '' })
  const sumCheckboxQuestions = useRef(0)
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | null)[][]>([])
  const checkboxRef = useRef<(HTMLInputElement | null)[][][]>([])

  const dispatch = useAppDispatch()
  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState
  const formState = useAppSelector((state) => state.form)
  const { data: globalForms, form } = formState
  const submissionState = useAppSelector((state) => state.submission)
  const { payload } = submissionState
  const navigate = useNavigate()
  const { formId } = useParams()

  useEffect(() => {
    if (formId) {
      populateData(formId)
    }
    // eslint-disable-next-line
  }, [formId])

  useEffect(() => {
    if (form) {
      if (form.isPublic) {
        getExistingSubmission(form._id)
      }

      const isFormSubmissionInProgress: boolean =
        form._id === payload.submission?.formId && !!getDirtySubmissionForm()

      if (!isFormSubmissionInProgress) {
        const newSubmission: NewSubmissionSchema = {
          formId: form._id,
          sections: form.sections
        }

        dispatch(setPayload({ submission: newSubmission, meta: { form } }))
      }

      if (payload.submission) {
        payload.submission.sections.forEach(({ questions }) => {
          questions.forEach(({ id, type, answer }) => {
            if (type === 'checkbox' && Array.isArray(answer)) {
              answer.forEach((option) => {
                manageFormCheckboxes(id, option)
              })
            }
          })
        })
      }

      setIsCheckboxMapReady(true)
    }
    // eslint-disable-next-line
  }, [form])

  useEffect(() => {
    if (payload.submission) {
      const allQuestions = payload.submission.sections.flatMap(({ questions }) => questions)

      setAllQuestions(allQuestions)
    }
  }, [payload.submission])

  const populateData = async (formId: string) => {
    const existingForm = form?._id === formId ? form : globalForms.find(({ _id }) => _id === formId)

    try {
      if (existingForm) {
        dispatch(setGlobalForm(existingForm))
      } else {
        setIsLoadingForm(true)
        await dispatch(fetchForm(formId)).unwrap()
      }
    } catch (error: any) {
      const errorObj = JSON.parse(error.message)
      if (errorObj.status === 403 || errorObj.status === 404) {
        setRequestState({ error: 'no_access' })
      } else {
        setRequestState({
          error: 'Error encountered processing the request'
        })
      }
    } finally {
      setIsLoadingForm(false)
    }
  }

  const getExistingSubmission = async (formId: string) => {
    try {
      setIsLoadingSubmissions(true)

      const existingSubmission = await dispatch(verifySubmission(formId)).unwrap()

      setExistingSubmission(existingSubmission)
    } catch (error: any) {
      setRequestState({
        error: 'Error encountered processing the request'
      })
    } finally {
      setIsLoadingSubmissions(false)
    }
  }

  const manageFormCheckboxes = (questionId: string, value: string, maxOptions?: number) => {
    const questionCheckboxes = allCheckboxes.current.get(questionId)

    const isMax = questionCheckboxes?.size === maxOptions || false

    questionCheckboxes
      ? allCheckboxes.current.set(questionId, manageSet(questionCheckboxes, value, isMax))
      : allCheckboxes.current.set(questionId, new Set<string>().add(value))
  }

  const getIsCheckboxOptionSelected = (questionId: string, option: string) => {
    const answerOptions = allCheckboxes.current.get(questionId)
    return answerOptions ? answerOptions.has(option) : false
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setAnswer(e.target.value)

  const handleCheckboxInputChange = (
    sectionId: string,
    question: QuestionSchema,
    option: string,
    indexes: {
      sectionIndex: number
      questionIndex: number
    }
  ) => {
    manageFormCheckboxes(question.id, option, question.meta?.checkboxOptions?.maxOptions)

    const questionCheckboxes = allCheckboxes.current.get(question.id)
    if (questionCheckboxes) {
      saveAnswer(sectionId, question, Array.from(questionCheckboxes))
    }

    // Reset option valid states
    const { sectionIndex: sIndex, questionIndex: qIndex } = indexes

    for (let i = 0; i < checkboxRef.current[sIndex][qIndex].length; i++) {
      if (checkboxRef.current[sIndex][qIndex][i]?.validationMessage) {
        checkboxRef.current[sIndex][qIndex][i]?.setCustomValidity('')
      }
    }
  }

  const handleRatingInputChange = (
    sectionId: string,
    question: QuestionSchema,
    rating: number,
    indexes: {
      sectionIndex: number
      questionIndex: number
    },
    answer?: number
  ) => {
    if (rating === answer) {
      const newSubmission: SubmissionSchema = JSON.parse(JSON.stringify(payload.submission))

      newSubmission.sections.forEach((section, sectionIndex) => {
        section.questions.forEach(({ id }, questionIndex) => {
          if (id === question.id) {
            delete newSubmission.sections[sectionIndex].questions[questionIndex].answer
          }
        })
      })

      dispatch(setPayload({ ...payload, submission: newSubmission }))
    } else {
      saveAnswer(sectionId, question, rating)
    }

    // Reset rating valid states
    const { sectionIndex: sIndex, questionIndex: qIndex } = indexes

    for (let i = 0; i < checkboxRef.current[sIndex][qIndex].length; i++) {
      if (checkboxRef.current[sIndex][qIndex][i]?.validationMessage) {
        checkboxRef.current[sIndex][qIndex][i]?.setCustomValidity('')
      }
    }
  }

  const handleInvalidCheckbox = (
    e: React.FormEvent<HTMLInputElement>,
    question: QuestionSchema
  ) => {
    if (e.target instanceof HTMLInputElement && e.target.validity.valueMissing) {
      const min = question.meta?.checkboxOptions?.minOptions
      const max = question.meta?.checkboxOptions?.maxOptions

      e.target.setCustomValidity(`Please select ${min === max ? min : `${min} to ${max}`} options.`)
    }
  }

  const handleInvalidRating = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement && e.target.validity.valueMissing) {
      e.target.setCustomValidity('Please select a value.')
    }
  }

  const handleFileChange = (e: any, question: QuestionSchema, sectionId: string) => {
    const file = e.target.files[0]

    // Update submissions
    const allSections: SectionSchema[] = JSON.parse(JSON.stringify(payload.submission.sections))
    const sectionIndex = allSections.findIndex(({ id }) => id === sectionId)
    const questionIndex = allSections[sectionIndex].questions.findIndex(
      ({ id }) => id === question.id
    )

    allSections[sectionIndex].questions[questionIndex].answer = file.name
    dispatch(
      setPayload({
        ...payload,
        submission: {
          ...payload.submission,
          sections: allSections
        }
      })
    )

    // Handle file data
    /* setFilesData((filesData) => {
      const newFile = {
        questionId: question.id,
        name: file.name,
        file: new FormData()
      }

      newFile.file.append(newFile.name, file)

      return [...filesData, newFile]
    }) */
  }

  const saveAnswer = (
    sectionId: string,
    question: QuestionSchema,
    providedAnswer?: AnswerTypeSchema
  ) => {
    const allSections: SectionSchema[] = JSON.parse(JSON.stringify(payload.submission.sections))
    const sectionIndex = allSections.findIndex(({ id }) => id === sectionId)
    const questionIndex = allSections[sectionIndex].questions.findIndex(
      ({ id }) => id === question.id
    )

    if (providedAnswer) {
      allSections[sectionIndex].questions[questionIndex].answer = providedAnswer
    } else if (question.type === 'number') {
      allSections[sectionIndex].questions[questionIndex].answer = Number(answer)
    } else {
      allSections[sectionIndex].questions[questionIndex].answer = answer
    }

    dispatch(
      setPayload({
        ...payload,
        submission: {
          ...payload.submission,
          sections: allSections
        }
      })
    )

    setAnswer('')
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (form) {
      navigate(`/form/${form._id}/review`, { viewTransition: true })
    }
  }
  const handleResetForm = () => {
    if (window.confirm('Reset progress?')) {
      dispatch(resetSubmission())

      for (let i = 0; i < inputRefs.current.length; i++) {
        for (let j = 0; j < inputRefs.current[i].length; j++) {
          if (inputRefs.current[i][j]?.value) {
            inputRefs.current[i][j]!.value = ''
          }
          if ((inputRefs.current[i][j] as HTMLInputElement)?.checked) {
            ;(inputRefs.current[i][j] as HTMLInputElement).checked = false
          }
        }
      }

      allCheckboxes.current.clear()
    }
  }

  const findQuestion = (questionId: string) => allQuestions?.find(({ id }) => id === questionId)

  const isOptionsRequired = (question: QuestionSchema) => {
    const minOptions = question.meta?.checkboxOptions?.minOptions
    const length = ((question.answer as string[]) || []).length

    if (minOptions !== undefined && length < minOptions) {
      return true
    }

    return false
  }

  return (
    <>
      {isLoadingForm || isLoadingSubmissions ? (
        <Spinner size='center' />
      ) : (
        <>
          {form?.isPublic && form.createdBy === authUser?._id ? (
            <Status info='You cannot respond to your own public survey' />
          ) : (
            <>
              {existingSubmission ? (
                <ConfirmResponseView form={form} submission={existingSubmission} />
              ) : (
                <div className='main_content form_content'>
                  {authUser && <NavBack theme={form?.meta.colour} />}
                  {requestState.error ? (
                    <>
                      {requestState.error === 'no_access' ? (
                        <Status warning='Form cannot be accessed' />
                      ) : (
                        <Status error={requestState.error} />
                      )}
                    </>
                  ) : (
                    <>
                      {form && payload.submission && (
                        <form
                          onSubmit={handleSubmit}
                          className={`theme-content-${form.meta.colour}`}>
                          <header>
                            <h1 className='heading-styled'>{form.name}</h1>
                            <p>{form.description}</p>
                          </header>
                          <span className='required'>* Indicates required question</span>
                          {payload.submission.sections.map((section, sectionIndex) => {
                            sumCheckboxQuestions.current = 0

                            return (
                              <section key={section.id}>
                                {section.meta?.showHeader && (
                                  <div className='header'>
                                    {section.name && <h2>{section.name}</h2>}
                                    {section.description && <p>{section.description}</p>}
                                  </div>
                                )}
                                {section.questions.map((question, questionIndex) => {
                                  if (question.conditional) {
                                    const { questionId, condition, value } = question.conditional

                                    const dependencyQuestion = findQuestion(questionId)

                                    if (dependencyQuestion) {
                                      if (
                                        condition === '>' &&
                                        !(Number(dependencyQuestion.answer) > Number(value))
                                      ) {
                                        return <Fragment key={questionIndex} />
                                      } else if (
                                        condition === '<' &&
                                        !(Number(dependencyQuestion.answer) < Number(value))
                                      ) {
                                        return <Fragment key={questionIndex} />
                                      }
                                    }
                                  }

                                  if (question.type === 'radio_checkbox') {
                                    sumCheckboxQuestions.current++
                                  }

                                  return (
                                    <div
                                      className='section_question section_question-options'
                                      key={questionIndex}>
                                      {question.type === 'radio_checkbox' ? (
                                        <div />
                                      ) : (
                                        <span>
                                          {questionIndex + 1 - sumCheckboxQuestions.current}.
                                        </span>
                                      )}
                                      <div>
                                        {question.type === 'radio_checkbox' ? (
                                          <div className='checkbox_container checkbox_container-radio checkbox_container-bg'>
                                            <div
                                              className={`radio-custom ${question.answer ? 'radio-custom-filled' : ''}`}>
                                              <input
                                                id={`${question.id}_${sectionIndex}_${questionIndex}`}
                                                type='checkbox'
                                                defaultChecked={question.answer as boolean}
                                                onChange={(e) =>
                                                  saveAnswer(section.id, question, e.target.checked)
                                                }
                                                required={question.required}
                                                ref={(el) => {
                                                  inputRefs.current[sectionIndex]
                                                    ? (inputRefs.current[sectionIndex][
                                                        questionIndex
                                                      ] = el)
                                                    : (inputRefs.current[sectionIndex] = [el])
                                                }}
                                              />
                                              <span />
                                              <label
                                                htmlFor={`${question.id}_${sectionIndex}_${questionIndex}`}>
                                                <span>{question.statement}</span>
                                                {question.description && (
                                                  <em> ({question.description})</em>
                                                )}
                                                {question.required && (
                                                  <span className='required'>*</span>
                                                )}
                                              </label>
                                            </div>
                                          </div>
                                        ) : (
                                          <label
                                            htmlFor={`${question.id}_${sectionIndex}_${questionIndex}`}>
                                            <span className='bold'>{question.statement}</span>
                                            {question.description && (
                                              <em> ({question.description})</em>
                                            )}
                                            {question.required && (
                                              <span className='required'>*</span>
                                            )}
                                          </label>
                                        )}
                                      </div>
                                      <div />
                                      {question.type === 'text' ? (
                                        <input
                                          id={`${question.id}_${sectionIndex}_${questionIndex}`}
                                          defaultValue={question.answer as string}
                                          onChange={handleInputChange}
                                          onBlur={() => saveAnswer(section.id, question)}
                                          required={question.required}
                                          minLength={question.meta?.textOptions?.minlength}
                                          maxLength={question.meta?.textOptions?.maxlength}
                                          placeholder={
                                            question.meta?.placeholder ?? 'Short text...'
                                          }
                                          ref={(el) => {
                                            inputRefs.current[sectionIndex]
                                              ? (inputRefs.current[sectionIndex][questionIndex] =
                                                  el)
                                              : (inputRefs.current[sectionIndex] = [el])
                                          }}
                                        />
                                      ) : question.type === 'textarea' ? (
                                        <textarea
                                          id={`${question.id}_${sectionIndex}_${questionIndex}`}
                                          defaultValue={question.answer as string}
                                          onChange={handleInputChange}
                                          onBlur={() => saveAnswer(section.id, question)}
                                          rows={TEXT_AREA_ROWS}
                                          required={question.required}
                                          minLength={question.meta?.textOptions?.minlength}
                                          maxLength={question.meta?.textOptions?.maxlength}
                                          placeholder={question.meta?.placeholder ?? 'Paragraph...'}
                                          ref={(el) => {
                                            inputRefs.current[sectionIndex]
                                              ? (inputRefs.current[sectionIndex][questionIndex] =
                                                  el)
                                              : (inputRefs.current[sectionIndex] = [el])
                                          }}
                                        />
                                      ) : question.type === 'number' ? (
                                        <input
                                          id={`${question.id}_${sectionIndex}_${questionIndex}`}
                                          type='number'
                                          defaultValue={question.answer as number}
                                          onChange={handleInputChange}
                                          onBlur={() => saveAnswer(section.id, question)}
                                          required={question.required}
                                          min={question.meta?.numberOptions?.min}
                                          max={question.meta?.numberOptions?.max}
                                          placeholder={
                                            question.meta?.placeholder ?? 'Enter number...'
                                          }
                                          ref={(el) => {
                                            inputRefs.current[sectionIndex]
                                              ? (inputRefs.current[sectionIndex][questionIndex] =
                                                  el)
                                              : (inputRefs.current[sectionIndex] = [el])
                                          }}
                                        />
                                      ) : question.type === 'email' ? (
                                        <input
                                          id={`${question.id}_${sectionIndex}_${questionIndex}`}
                                          type='email'
                                          defaultValue={question.answer as string}
                                          onChange={handleInputChange}
                                          onBlur={() => saveAnswer(section.id, question)}
                                          required={question.required}
                                          minLength={question.meta?.emailOptions?.minlength}
                                          maxLength={question.meta?.emailOptions?.maxlength}
                                          placeholder={
                                            question.meta?.placeholder ?? 'Enter email...'
                                          }
                                          ref={(el) => {
                                            inputRefs.current[sectionIndex]
                                              ? (inputRefs.current[sectionIndex][questionIndex] =
                                                  el)
                                              : (inputRefs.current[sectionIndex] = [el])
                                          }}
                                        />
                                      ) : question.type === 'tel' ? (
                                        <input
                                          id={`${question.id}_${sectionIndex}_${questionIndex}`}
                                          type='tel'
                                          defaultValue={question.answer as string}
                                          onChange={handleInputChange}
                                          onBlur={() => saveAnswer(section.id, question)}
                                          required={question.required}
                                          minLength={question.meta?.telOptions?.minlength}
                                          maxLength={question.meta?.telOptions?.maxlength}
                                          placeholder={
                                            question.meta?.placeholder ?? 'Enter phone...'
                                          }
                                          ref={(el) => {
                                            inputRefs.current[sectionIndex]
                                              ? (inputRefs.current[sectionIndex][questionIndex] =
                                                  el)
                                              : (inputRefs.current[sectionIndex] = [el])
                                          }}
                                        />
                                      ) : question.type === 'date' ? (
                                        <input
                                          id={`${question.id}_${sectionIndex}_${questionIndex}`}
                                          type='date'
                                          defaultValue={question.answer as string}
                                          onChange={handleInputChange}
                                          onBlur={() => saveAnswer(section.id, question)}
                                          required={question.required}
                                          min={
                                            question.meta?.dateOptions?.min &&
                                            setToCalendarDate(question.meta.dateOptions.min)
                                          }
                                          max={
                                            question.meta?.dateOptions?.max &&
                                            setToCalendarDate(question.meta.dateOptions.max)
                                          }
                                          ref={(el) => {
                                            inputRefs.current[sectionIndex]
                                              ? (inputRefs.current[sectionIndex][questionIndex] =
                                                  el)
                                              : (inputRefs.current[sectionIndex] = [el])
                                          }}
                                        />
                                      ) : question.type === 'dropdown' ? (
                                        <div className='checkbox_container'>
                                          {question.meta?.dropdownOptions?.values.map(
                                            (option, optionIndex) => (
                                              <div
                                                className={`radio-custom ${
                                                  question.answer === option
                                                    ? 'radio-custom-filled'
                                                    : ''
                                                }`}
                                                key={optionIndex}>
                                                <input
                                                  id={`${question.id}_${sectionIndex}_${questionIndex}_${optionIndex}`}
                                                  type='radio'
                                                  name={question.id}
                                                  value={option}
                                                  checked={question.answer === option}
                                                  onChange={(e) =>
                                                    saveAnswer(section.id, question, e.target.value)
                                                  }
                                                  required={question.required}
                                                  ref={(el) => {
                                                    inputRefs.current[sectionIndex]
                                                      ? (inputRefs.current[sectionIndex][
                                                          questionIndex
                                                        ] = el)
                                                      : (inputRefs.current[sectionIndex] = [el])
                                                  }}
                                                />
                                                <span />
                                                <label
                                                  htmlFor={`${question.id}_${sectionIndex}_${questionIndex}_${optionIndex}`}>
                                                  {option}
                                                </label>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      ) : question.type === 'checkbox' ? (
                                        <>
                                          {isCheckboxMapReady && (
                                            <div className='checkbox_container'>
                                              {question.meta?.checkboxOptions?.values.map(
                                                (option, optionIndex) => {
                                                  const isCheckboxOptionSelected =
                                                    getIsCheckboxOptionSelected(question.id, option)

                                                  return (
                                                    <div
                                                      className={`radio-custom ${isCheckboxOptionSelected ? 'radio-custom-filled' : ''}`}
                                                      key={optionIndex}>
                                                      <input
                                                        id={`${question.id}_${sectionIndex}_${questionIndex}_${optionIndex}`}
                                                        type='checkbox'
                                                        checked={isCheckboxOptionSelected}
                                                        onChange={() => {
                                                          handleCheckboxInputChange(
                                                            section.id,
                                                            question,
                                                            option,
                                                            {
                                                              sectionIndex,
                                                              questionIndex
                                                            }
                                                          )
                                                        }}
                                                        required={
                                                          question.required &&
                                                          isOptionsRequired(question)
                                                        }
                                                        onInvalid={(e) =>
                                                          handleInvalidCheckbox(e, question)
                                                        }
                                                        ref={(el) => {
                                                          checkboxRef.current[sectionIndex]
                                                            ? checkboxRef.current[sectionIndex][
                                                                questionIndex
                                                              ]
                                                              ? (checkboxRef.current[sectionIndex][
                                                                  questionIndex
                                                                ][optionIndex] = el)
                                                              : (checkboxRef.current[sectionIndex][
                                                                  questionIndex
                                                                ] = [el])
                                                            : (checkboxRef.current[sectionIndex] = [
                                                                []
                                                              ])
                                                        }}
                                                      />
                                                      <span />
                                                      <label
                                                        htmlFor={`${question.id}_${sectionIndex}_${questionIndex}_${optionIndex}`}>
                                                        {option}
                                                      </label>
                                                    </div>
                                                  )
                                                }
                                              )}
                                            </div>
                                          )}
                                        </>
                                      ) : question.type === 'rating' ? (
                                        <div className='checkbox_container rating-picker'>
                                          {question.meta?.ratingOptions?.map(
                                            (rating, ratingIndex) => (
                                              <div
                                                className={`radio-custom ${
                                                  rating <= (question.answer as number)
                                                    ? 'radio-custom-filled'
                                                    : ''
                                                }`}
                                                onClick={() =>
                                                  handleRatingInputChange(
                                                    section.id,
                                                    question,
                                                    rating,
                                                    {
                                                      sectionIndex,
                                                      questionIndex
                                                    },
                                                    question.answer as number
                                                  )
                                                }
                                                key={rating}
                                                title={`${rating} / ${question.meta?.ratingOptions?.length}`}>
                                                <input
                                                  type='checkbox'
                                                  checked={rating <= (question.answer as number)}
                                                  onChange={() => {}}
                                                  required={question.required && !question.answer}
                                                  onInvalid={handleInvalidRating}
                                                  ref={(el) => {
                                                    checkboxRef.current[sectionIndex]
                                                      ? checkboxRef.current[sectionIndex][
                                                          questionIndex
                                                        ]
                                                        ? (checkboxRef.current[sectionIndex][
                                                            questionIndex
                                                          ][ratingIndex] = el)
                                                        : (checkboxRef.current[sectionIndex][
                                                            questionIndex
                                                          ] = [el])
                                                      : (checkboxRef.current[sectionIndex] = [[]])
                                                  }}
                                                />
                                                <span />
                                              </div>
                                            )
                                          )}
                                        </div>
                                      ) : (
                                        question.type === 'file' && (
                                          <input
                                            type='file'
                                            onChange={(e) =>
                                              handleFileChange(e, question, section.id)
                                            }
                                            required={question.required}
                                          />
                                        )
                                      )}
                                    </div>
                                  )
                                })}
                              </section>
                            )
                          })}
                          <div className='form_footer form_footer-double-stc form_footer-double-0fg'>
                            <div className={`${!getDirtySubmissionForm() ? 'disabled' : ''}`}>
                              <div className='div-with-icon' onClick={handleResetForm}>
                                <span className='material-symbols-rounded'>undo</span>
                                <span>Reset progress</span>
                              </div>
                            </div>
                            <button className='submit button-with-icon submit-with-icon'>
                              <span>Review</span>
                              <span className='material-symbols-rounded'>arrow_right_alt</span>
                            </button>
                          </div>
                        </form>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}
